<template>
  <component :is="isComponent ? 'div' : 'UPage'">
    <ConfigHeader
      v-if="!isComponent"
      :back-to="`/customers/${customerNo}/tenants`"
      :last-editor="config.lastEditor"
      :last-update="config.lastUpdate"
      :saving="saving"
      :tenant-name="tenantName"
      title="Mail Konfig"
      @save="saveConfig"
    />

    <component :is="isComponent ? 'div' : 'UPageBody'" :class="{ 'p-0': isComponent }">
      <div v-if="loading" class="flex justify-center py-6">
        <UIcon class="w-8 h-8 animate-spin text-muted" name="i-lucide-loader-circle" />
      </div>

      <div v-else class="space-y-6">
        <!-- Status & Activation -->
        <ConfigStatusCard
          :model-value="config.active"
          :disabled="!canWrite"
          title="Mail Automatisierung"
          description="Aktivieren oder deaktivieren Sie die Mail-Anbindung für diesen Mandanten."
          icon="i-lucide-mail"
          @update:model-value="onActiveToggle"
        />

        <USeparator class="my-8" />

        <ConfigChangelogAlert
          description="Bitte stellen Sie sicher, dass Ihre Mail-Konfiguration die aktuellen Systemanforderungen erfüllt."
        />

        <!-- Einrichtungs-Informationen -->
        <UCard
          :ui="{
            root: isInfoOpen ? '' : 'divide-y-0',
            body: isInfoOpen ? 'p-6' : 'hidden',
            header: 'px-4 py-3 sm:px-6',
          }"
        >
          <template #header>
            <div
              class="flex items-center justify-between cursor-pointer"
              @click="isInfoOpen = !isInfoOpen"
            >
              <div class="flex items-center gap-3">
                <UIcon name="i-lucide-info" class="h-5 w-5 text-primary" />
                <h3 class="text-base font-semibold">Einrichtung und Verbindung testen</h3>
              </div>
              <UButton
                :icon="isInfoOpen ? 'i-lucide-chevron-up' : 'i-lucide-chevron-down'"
                color="neutral"
                variant="ghost"
                @click.stop="isInfoOpen = !isInfoOpen"
              />
            </div>
          </template>

          <div v-show="isInfoOpen" class="space-y-6">
            <ConfigDocsAlert
              title="Einrichtung der Mail Komponente"
              description="Bitte lesen Sie die vollständigen Anweisungen zur Einrichtung in unserer Dokumentation. Dort finden Sie alle Details zur Registrierung der App in Microsoft Entra ID."
              doc-key="system-config.invoice.mail-configuration"
              tenant-doc-key="tenant-config.mail-portal-config"
            />

            <UAlert
              icon="i-lucide-lightbulb"
              title="Aktivierung"
              description="Es wird empfohlen, die Komponente erst nach einem erfolgreichen Verbindungstest zu aktivieren."
              color="warning"
              variant="subtle"
              :ui="{
                title: 'text-warning-900 dark:text-warning-200',
                description: 'text-warning-800 dark:text-warning-300',
              }"
            />
          </div>
        </UCard>

        <!-- Graph API Konfiguration -->
        <UCard
          :ui="{
            root: isGraphConfigOpen ? '' : 'divide-y-0',
            body: isGraphConfigOpen ? 'p-6' : 'hidden',
            header: 'px-4 py-3 sm:px-6',
          }"
        >
          <template #header>
            <div
              class="flex items-center justify-between cursor-pointer"
              @click="isGraphConfigOpen = !isGraphConfigOpen"
            >
              <div class="flex items-center gap-3">
                <UIcon name="i-lucide-key" class="h-5 w-5 text-primary" />
                <h3 class="text-base font-semibold">Microsoft Graph API</h3>
              </div>
              <UButton
                :icon="isGraphConfigOpen ? 'i-lucide-chevron-up' : 'i-lucide-chevron-down'"
                color="neutral"
                variant="ghost"
                @click.stop="isGraphConfigOpen = !isGraphConfigOpen"
              />
            </div>
          </template>

          <div v-show="isGraphConfigOpen" class="space-y-6">
            <div class="grid grid-cols-1 md:grid-cols-2 gap-6">
              <UFormField label="Mandanten-ID (Tenant ID)" required>
                <UInput
                  v-model="config.tenantId"
                  :disabled="!canWrite"
                  class="w-full"
                  placeholder="00000000-0000-0000-0000-000000000000"
                />
              </UFormField>

              <UFormField label="Client ID" required>
                <UInput
                  v-model="config.clientId"
                  :disabled="!canWrite"
                  class="w-full"
                  placeholder="00000000-0000-0000-0000-000000000000"
                />
              </UFormField>
            </div>

            <div class="grid grid-cols-1 md:grid-cols-2 gap-6">
              <UFormField label="Client Secret" required>
                <UInput
                  v-model="config.clientSecret"
                  :disabled="!canWrite"
                  class="w-full"
                  placeholder="••••••••"
                  type="password"
                />
              </UFormField>
            </div>
          </div>
        </UCard>

        <!-- Postfach & Ordner -->
        <UCard
          :ui="{
            root: isMailboxConfigOpen ? '' : 'divide-y-0',
            body: isMailboxConfigOpen ? 'p-6' : 'hidden',
            header: 'px-4 py-3 sm:px-6',
          }"
        >
          <template #header>
            <div
              class="flex items-center justify-between cursor-pointer"
              @click="isMailboxConfigOpen = !isMailboxConfigOpen"
            >
              <div class="flex items-center gap-3">
                <UIcon name="i-lucide-mail" class="h-5 w-5 text-primary" />
                <h3 class="text-base font-semibold">Postfach & Ordnerstruktur Eingangsrechnung</h3>
              </div>
              <UButton
                :icon="isMailboxConfigOpen ? 'i-lucide-chevron-up' : 'i-lucide-chevron-down'"
                color="neutral"
                variant="ghost"
                @click.stop="isMailboxConfigOpen = !isMailboxConfigOpen"
              />
            </div>
          </template>

          <div v-show="isMailboxConfigOpen" class="space-y-6">
            <!-- Postfach E-Mail -->
            <div class="grid grid-cols-1 md:grid-cols-2 gap-6">
              <UFormField label="Postfach E-Mail">
                <UInput
                  v-model="config.mailAccount"
                  :disabled="!canWrite"
                  class="w-full"
                  placeholder="z. B. eingang@kunde.de"
                />
              </UFormField>
            </div>

            <UDivider />

            <!-- Ordnerstruktur -->
            <div>
              <p class="text-sm text-muted mb-4">
                Bitte geben Sie die Ordnernamen genau so an, wie sie im Postfach existieren.
              </p>
              <div class="grid grid-cols-1 md:grid-cols-2 gap-6">
                <UFormField description="E-Mails werden von hier abgeholt" label="Abhol-Ordner">
                  <UInput v-model="config.fetchFolder" :disabled="!canWrite" class="w-full" placeholder="z. B. Inbox" />
                </UFormField>
                <UFormField
                  description="E-Mails werden abgelegt wenn sie in Bearbeitung sind"
                  label="In Bearbeitung-Ordner"
                >
                  <UInput
                    v-model="config.progressFolder"
                    :disabled="!canWrite"
                    class="w-full"
                    placeholder="z. B. In Bearbeitung"
                  />
                </UFormField>
                <UFormField
                  description="Verarbeitete E-Mails werden hierhin verschoben"
                  label="Archiv-Ordner"
                >
                  <UInput
                    v-model="config.archiveFolder"
                    :disabled="!canWrite"
                    class="w-full"
                    placeholder="z. B. Archived"
                  />
                </UFormField>
                <UFormField
                  description="Fehlerhafte oder abgelehnte E-Mails landen hier"
                  label="Abgelehnt-Ordner"
                >
                  <UInput
                    v-model="config.rejectedFolder"
                    :disabled="!canWrite"
                    class="w-full"
                    placeholder="z. B. Rejected"
                  />
                </UFormField>
              </div>
            </div>
          </div>
        </UCard>

        <!-- Absender Adresse -->
        <UCard
          :ui="{
            root: isSenderConfigOpen ? '' : 'divide-y-0',
            body: isSenderConfigOpen ? 'p-6' : 'hidden',
            header: 'px-4 py-3 sm:px-6',
          }"
        >
          <template #header>
            <div
              class="flex items-center justify-between cursor-pointer"
              @click="isSenderConfigOpen = !isSenderConfigOpen"
            >
              <div class="flex items-center gap-3">
                <UIcon name="i-lucide-send" class="h-5 w-5 text-primary" />
                <h3 class="text-base font-semibold">Absender Adresse für Benachrichtigungen</h3>
              </div>
              <UButton
                :icon="isSenderConfigOpen ? 'i-lucide-chevron-up' : 'i-lucide-chevron-down'"
                color="neutral"
                variant="ghost"
                @click.stop="isSenderConfigOpen = !isSenderConfigOpen"
              />
            </div>
          </template>

          <div v-show="isSenderConfigOpen" class="space-y-6">
            <div class="grid grid-cols-1 md:grid-cols-2 gap-6">
              <UFormField label="Absender E-Mail">
                <UInput
                  v-model="config.mailSenderAddress"
                  :disabled="!canWrite"
                  class="w-full"
                  placeholder="z. B. system@kunde.de"
                />
              </UFormField>
            </div>
          </div>
        </UCard>
        <UCard
          :ui="{
            root: isTestOpen ? '' : 'divide-y-0',
            body: isTestOpen ? 'p-6' : 'hidden',
            header: 'px-4 py-3 sm:px-6',
          }"
        >
          <template #header>
            <div
              class="flex items-center justify-between cursor-pointer"
              @click="isTestOpen = !isTestOpen"
            >
              <div class="flex items-center gap-3">
                <UIcon name="i-lucide-zap" class="h-5 w-5 text-primary" />
                <h3 class="text-base font-semibold">Verbindungstest</h3>
              </div>
              <UButton
                :icon="isTestOpen ? 'i-lucide-chevron-up' : 'i-lucide-chevron-down'"
                color="neutral"
                variant="ghost"
                @click.stop="isTestOpen = !isTestOpen"
              />
            </div>
          </template>

          <div v-show="isTestOpen" class="space-y-6">
            <div
              class="flex flex-col sm:flex-row sm:items-center gap-6 p-4 rounded-lg border border-primary-200 bg-primary-50/50 dark:border-primary-800 dark:bg-primary-900/20"
            >
              <div class="flex-1">
                <h4 class="font-bold">Mail Verbindungstest</h4>
                <p class="text-sm text-muted mt-1">
                  Überprüfen Sie den Zugriff auf das Postfach und die Ordnerstruktur.
                </p>
                <div class="mt-4">
                  <UCheckbox
                    v-model="runWriteTest"
                    :disabled="!canWrite"
                    label="Optionalen Schreib- und Sendetest durchführen"
                    description="Erzeugt eine Test-Mail im Postfach, verschiebt sie und löscht sie wieder. Bei gesetzter Absender Adresse für Benachrichtigungen wird das Versenden getestet."
                  />
                </div>
              </div>
              <UButton
                v-if="canWrite"
                :loading="testingCredentials"
                color="primary"
                icon="i-lucide-zap"
                label="Zugang testen"
                @click="testCredentials"
              />
            </div>

            <div
              v-if="testResult"
              class="rounded-lg border p-4 transition-all duration-300"
              :class="
                testResult.success
                  ? 'border-green-200 bg-green-50 dark:border-green-800 dark:bg-green-900/20'
                  : 'border-red-200 bg-red-50 dark:border-red-800 dark:bg-red-900/20'
              "
            >
              <div class="flex items-center gap-3 mb-6">
                <UIcon
                  :name="testResult.success ? 'i-lucide-check-circle' : 'i-lucide-x-circle'"
                  :class="testResult.success ? 'text-green-500' : 'text-red-500'"
                  class="w-8 h-8"
                />
                <span
                  :class="
                    testResult.success
                      ? 'text-green-700 dark:text-green-300'
                      : 'text-red-700 dark:text-red-300'
                  "
                  class="font-bold text-lg"
                >
                  {{ testResult.message }}
                </span>
              </div>

              <div
                v-if="testResult.checks"
                class="grid gap-4 sm:grid-cols-2 mt-6 border-t border-gray-200 dark:border-gray-800 pt-6"
              >
                <div
                  v-for="check in testResult.checks"
                  :key="check.label"
                  class="flex gap-4 p-4 rounded-lg bg-white/50 dark:bg-gray-900/50 border border-gray-100 dark:border-gray-800"
                >
                  <div class="mt-0.5 shrink-0">
                    <UIcon
                      :name="
                        check.status === 'success' ? 'i-lucide-check-circle' : 'i-lucide-x-circle'
                      "
                      :class="check.status === 'success' ? 'text-green-500' : 'text-red-500'"
                      class="w-5 h-5"
                    />
                  </div>
                  <div>
                    <p class="font-bold text-gray-900 dark:text-white text-sm">{{ check.label }}</p>
                    <p class="text-gray-600 dark:text-gray-400 text-xs mt-1 leading-relaxed">
                      {{ check.detail }}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </UCard>
      </div>
    </component>

    <!-- Activation/Deactivation Confirmation Modal -->
    <ConfirmationModal
      v-model:open="isConfirmModalOpen"
      :description="
        config.active
          ? 'Bestätigen Sie die Deaktivierung der Mail-Komponente.'
          : 'Bestätigen Sie die Aktivierung der Mail-Komponente.'
      "
      :message="
        config.active
          ? 'Möchten Sie die Mail-Komponente wirklich deaktivieren?'
          : !testResult?.success
            ? 'Möchten Sie die Mail-Komponente wirklich aktivieren? ACHTUNG: Der Verbindungstest war nicht erfolgreich. Die Komponente funktioniert möglicherweise nicht korrekt.'
            : 'Möchten Sie die Mail-Komponente wirklich aktivieren?'
      "
      :title="config.active ? 'Mail deaktivieren' : 'Mail aktivieren'"
      @confirm="confirmToggle"
    />
    <ConfirmationModal
      v-model:open="isConfirmSaveModalOpen"
      title="Konfiguration speichern"
      description="Bestätigen Sie das Speichern der Konfiguration."
      message="ACHTUNG: Der Verbindungstest war nicht erfolgreich. Die Komponente funktioniert möglicherweise nicht korrekt. Möchten Sie die Konfiguration trotzdem speichern?"
      @confirm="confirmSave"
    />
  </component>
</template>

<script lang="ts" setup>
import { onMounted, ref, watch } from 'vue'
import axios from 'axios'
import ConfirmationModal from '@/components/ConfirmationModal.vue'

interface MailConfig {
  active: boolean
  mailAccount: string
  mailSenderAddress: string
  fetchFolder: string
  progressFolder: string
  archiveFolder: string
  rejectedFolder: string
  clientId: string
  clientSecret: string
  tenantId: string
  lastUpdate?: string
  lastEditor?: string
}

interface TestCheck {
  label: string
  status: 'success' | 'warning' | 'error' | 'neutral'
  detail: string
}

interface TestResult {
  ok: boolean
  message: string
  checks: TestCheck[]
}

defineProps({
  customerNo: {
    type: String,
    required: true,
  },
  tenantName: {
    type: String,
    required: true,
  },
  isComponent: {
    type: Boolean,
    default: false,
  },
  canWrite: {
    type: Boolean,
    default: true,
  },
})

const emit = defineEmits(['dirty'])

const toast = useToast()

const loading = ref(true)
const saving = ref(false)
const testingCredentials = ref(false)
const isConfirmModalOpen = ref(false)
const isConfirmSaveModalOpen = ref(false)
const isInfoOpen = ref(false)
const isGraphConfigOpen = ref(false)
const isMailboxConfigOpen = ref(false)
const isSenderConfigOpen = ref(false)
const isTestOpen = ref(false)
const runWriteTest = ref(false)
const testResult = ref<{ success: boolean; message: string; checks?: TestCheck[] } | null>(null)

defineExpose({
  saveConfig: () => saveConfig(),
})

const config = ref<MailConfig>({
  active: false,
  mailAccount: '',
  mailSenderAddress: '',
  fetchFolder: '',
  progressFolder: '',
  archiveFolder: '',
  rejectedFolder: '',
  clientId: '',
  clientSecret: '',
  tenantId: '',
})

const onActiveToggle = () => {
  isConfirmModalOpen.value = true
}

const confirmToggle = () => {
  config.value.active = !config.value.active
  isConfirmModalOpen.value = false
}

const confirmSave = () => {
  isConfirmSaveModalOpen.value = false
  saveConfig(true)
}

const fetchConfig = async () => {
  loading.value = true
  try {
    const response = await axios.get<MailConfig>('/api/mail/v1/config')
    if (response.data) {
      config.value = { ...config.value, ...response.data }
    }
  } catch (error) {
    console.error('Failed to fetch mail config', error)
    toast.add({
      title: 'Fehler',
      description: 'Fehler beim Laden der Konfiguration.',
      color: 'danger',
    })
  } finally {
    loading.value = false
  }
}

const saveConfig = async (force = false) => {
  if (!force && config.value.active && testResult.value?.success === false) {
    isConfirmSaveModalOpen.value = true
    return
  }
  saving.value = true
  try {
    await axios.put('/api/mail/v1/config', config.value)
    toast.add({ title: 'Erfolg', description: 'Konfiguration gespeichert.', color: 'success' })
    await fetchConfig()
  } catch (error) {
    console.error('Failed to save mail config', error)
    toast.add({
      title: 'Fehler',
      description: 'Fehler beim Speichern der Konfiguration.',
      color: 'danger',
    })
  } finally {
    saving.value = false
  }
}

const testCredentials = async () => {
  if (!config.value.mailAccount) {
    toast.add({
      title: 'Fehlende Angaben',
      description: 'Bitte füllen Sie das Feld "Postfach E-Mail" aus.',
      color: 'danger',
    })
    return
  }

  testingCredentials.value = true
  testResult.value = null
  try {
    const response = await axios.post<TestResult>('/api/mail/v1/config/test', {
      clientId: config.value.clientId || '',
      clientSecret: config.value.clientSecret || '',
      tenantId: config.value.tenantId || '',
      mailAccount: config.value.mailAccount || '',
      fetchFolder: config.value.fetchFolder || '',
      inprogressFolder: config.value.progressFolder || '',
      archiveFolder: config.value.archiveFolder || '',
      errorFolder: config.value.rejectedFolder || '',
      mailSenderAddress: config.value.mailSenderAddress || '',
      runWriteTest: runWriteTest.value,
    })
    testResult.value = {
      success: response.data.ok,
      message: response.data.message,
      checks: response.data.checks,
    }
  } catch (error: unknown) {
    let msg = 'Unbekannter Fehler'
    let checks: TestCheck[] | undefined = undefined
    if (axios.isAxiosError(error)) {
      if (error.response?.data && typeof error.response.data === 'object') {
        msg = error.response.data.message || error.message
        checks = error.response.data.checks
      } else {
        msg = error.response?.data || error.message
      }
    } else if (error instanceof Error) {
      msg = error.message
    }
    testResult.value = { success: false, message: `Fehler: ${msg}`, checks }
  } finally {
    testingCredentials.value = false
  }
}

watch(
  config,
  () => {
    emit('dirty')
  },
  { deep: true },
)

onMounted(() => {
  fetchConfig()
})
</script>
